import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Chip, ErrorState, Skeleton, Tooltip } from '../ui';
import {
    benchmarkPhrase, benchmarkWho, compareTakeaway, selectionSubject,
} from '../../utils/branchCompare';
import {
    MOMENTUM_MONTHS, MOS_LEGEND, MOS_OVER, MOS_STATUS, MOS_TIGHT, PERF_BANDS, PERF_CARRY_REL, PERF_GROUPS, PERF_OPENINGS,
    SELL_THROUGH_DAYS, addPerfPools, buildPerfRows, formatDay, formatMoneyShort, perfBenchmarkOf, perfPoolsByBranch, sellRateSeries,
} from '../../utils/branchPerformance';
import {
    CompareHero, CompareKey, GAPS_TIP, MeasureCard, chosenHow,
} from './ComparePanel';
import { LineChart } from './charts';

const n = (v) => (v || 0).toLocaleString('en-US');

const TITLE_TIP = 'Based on used units sold and received; each unit counts at its current branch.';
const VERDICT_TIP = 'Better or Worse is judged in the direction that is good for the business for each measure (for example, faster turns are better), '
    + 'on the figures as shown. '
    + `A shown difference under ${PERF_BANDS.turns.toFixed(1)}× for turns (so 0.1× is Similar), ${PERF_BANDS.momentum} pts for momentum, `
    + `${PERF_BANDS.sellThrough.toFixed(1)} pt for shares, ${PERF_BANDS.medianDays} days for days to sell or ${PERF_CARRY_REL * 100}% of the benchmark `
    + 'for carrying cost counts as Similar. '
    + 'Months of supply has no Better or Worse: its chip says whether the stock is Tight, Healthy or Overstocked.';
const PERF_GAPS_TIP = `${GAPS_TIP} Months of supply is left out too.`;
const RATE_TIP = 'Each calendar month, the used units sold as a share of the used units in stock at the start of the 1st. '
    + 'Higher means stock is selling faster. The chart shows the last 12 complete months, so the current month is left out until it ends; '
    + 'the cards above use the 12 months up to the as-of date.';
const SMALL_TIP = 'Fewer than 5 units or sales behind this figure: a single unit moves it a lot, so treat the difference with care.';
const LOADING_INV = 'Loading inventory…';

/**
 * The visible key of months of supply, on its card: what the figure means
 * and the three status chips with their thresholds (the same chips as the
 * card's status line).
 */
export function MosLegend({ single = true }) {
    return (
        <div className="br2-mosx" data-testid="mos-explain">
            <p className="br2-mosx-t">
                <b>Months of supply</b> is how long the current stock lasts at {single ? "this branch's" : "the selection's"} sales pace: units in stock ÷ units sold per month.
            </p>
            <ul className="br2-mosx-l" data-testid="mos-legend" aria-label="Months of supply statuses">
                {MOS_LEGEND.map((l) => (
                    <li key={l.status}>
                        <Chip tone={MOS_STATUS[l.status].tone} className="br2-mosx-chip">{MOS_STATUS[l.status].word}</Chip>
                        <span>{l.text}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

MosLegend.propTypes = { single: PropTypes.bool };

/**
 * The chart's drawing width follows its box (1 SVG unit = 1 px), so the axis
 * text stays readable from a phone to a wide screen; 720 until measured.
 */
const useBoxWidth = (fallback = 720) => {
    const ref = useRef(null);
    const [width, setWidth] = useState(fallback);
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(([entry]) => {
            const w = Math.round(entry.contentRect.width);
            if (w > 0) setWidth(Math.max(300, w));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return [ref, width];
};

/** Monthly sell rate: the selection's line against the benchmark's. */
function SellRateChart({
    series, win, selectionName, benchName, median,
}) {
    const [boxRef, width] = useBoxWidth();
    const pct = (v) => `${Number(v.toFixed(1))}%`;
    const lines = [
        { name: selectionName, tone: 'teal', emphasis: true, values: series.sel.map((p) => p.rate), pts: series.sel },
        { name: benchName, tone: 'ink', dashed: true, values: series.bench.map((p) => p.rate), pts: series.bench },
    ];
    const formatTip = (v, i, s) => {
        const p = s.pts?.[i];
        if (!p) return `${v.toFixed(1)}%`;
        if (p.sales == null) return `${v.toFixed(1)}% · median of ${n(p.branches)} branches`;
        return `${v.toFixed(1)}% · ${n(p.sales)} sold of ${n(p.stock)} in stock on the 1st`;
    };
    const { cal } = win;
    return (
        <div className="br2-perf-chart" data-testid="perf-chart">
            <div className="br2-row">
                <h3 id="br2-perfrate-h">Monthly sell rate</h3>
                <Tooltip label="About Monthly sell rate" content={RATE_TIP} />
            </div>
            <div className="br2-legend" aria-hidden="true">
                <span><i className="br2-ln-key br2-ln-key--teal br2-ln-key--strong" />{selectionName}</span>
                <span><i className="br2-ln-key br2-ln-key--ink-dash" />{benchName}{median ? ' (median of the branch rates)' : ''}</span>
            </div>
            <div ref={boxRef} className="br2-perf-chartw">
                <LineChart
                    label={`Monthly sell rate, ${selectionName} and ${benchName}`}
                    labels={cal.labels}
                    subLabels={cal.years}
                    pointLabels={cal.titles}
                    series={lines}
                    formatY={pct}
                    formatTip={formatTip}
                    tipTitle={(i) => cal.titles[i]}
                    fromZero
                    width={width}
                    height={252}
                    endLabels={false}
                />
            </div>
        </div>
    );
}

SellRateChart.propTypes = {
    series: PropTypes.object.isRequired,
    win: PropTypes.object.isRequired,
    selectionName: PropTypes.string.isRequired,
    benchName: PropTypes.string.isRequired,
    median: PropTypes.bool,
};

const BENCH_HOW = {
    dealership: 'Whole dealership adds up the sales and stock of every branch, this selection included, before working out each figure.',
    others: 'Other branches adds up the sales and stock of every branch except the selection before working out each figure.',
    median: 'Median branch works out each figure per branch and takes the middle one (branches with no data for a measure are left out).',
};

function HowToRead({ basis, oldest, chosenNames = [] }) {
    return (
        <details className="br2-cmp-how" data-testid="perf-howto">
            <summary>How to read this</summary>
            <ul>
                <li>
                    Sales are used units sold in the last 12 months up to the as-of date (the cards). The monthly sell rate chart uses the last 12 complete
                    calendar months instead. Each unit counts at the branch it is at today, or was sold from, for its whole stay; transfers between branches are
                    not tracked.
                </li>
                <li>Stock on a day is every used unit received by then and not yet sold. Average stock is the mean of the 12 month-end counts.</li>
                <li>Used turns: 12-month sales ÷ average stock. Higher is better.</li>
                <li>
                    Sales momentum: average monthly sales in the last {MOMENTUM_MONTHS} months against the 12-month monthly average. Above 0% the pace is
                    picking up.
                </li>
                <li>{SELL_THROUGH_DAYS}-day sell-through: of the units in stock {SELL_THROUGH_DAYS} days ago, the share sold since.</li>
                <li>Median days to sell: the middle of the received-to-sold times of the last 12 months. Units sold the day they arrived count as sales but not here.</li>
                <li>
                    Months of supply: how long today&apos;s used stock lasts at the sales pace of the last 12 months (units in stock ÷ units sold per
                    month), judged on the figure as shown. Under {MOS_TIGHT}.0 is Tight: the stock may run short before new units arrive.{' '}
                    {MOS_TIGHT}.0 to {MOS_OVER}.0 (both included) is Healthy. Over {MOS_OVER}.0 is Overstocked: more stock than the pace sells, which
                    ties up capital and builds carrying cost. As a guide, about 2.5 to 3 used turns a year is considered good in the industry, which is
                    roughly 4 to 5 months of supply. Above 24 months the figure is shown as 24+.
                </li>
                <li>
                    Money tied up comes from today&apos;s inventory: the share of the used asking-price value older than {oldest} days (weighted by price, so it
                    can differ from the unit count above), and the carrying cost built up so far per used unit.
                </li>
                <li>
                    Each card&apos;s foot line says what it counts: units in stock today, sales in 12 months or the average stock. The &quot;Branch vs&quot; panel above also counts new and
                    unpriced units, so its unit counts are higher.
                </li>
                <li>{basis === 'chosen' ? chosenHow(chosenNames, 'sales and stock') : BENCH_HOW[basis]}</li>
            </ul>
        </details>
    );
}

HowToRead.propTypes = {
    basis: PropTypes.string.isRequired, oldest: PropTypes.number.isRequired, chosenNames: PropTypes.arrayOf(PropTypes.string),
};

/** The n line of a money card: "$4.2M aged · 89 priced used · dealership $21.0M". */
const moneyNLine = (r, benchShort, what) => {
    const lead = r.money != null ? `${formatMoneyShort(r.money)} ${what} · ` : '';
    if (r.benchNoun === 'branches') return `${lead}${n(r.selN)} ${r.nNoun} · median of ${n(r.benchN)} branches`;
    const bm = r.benchMoney != null ? ` ${formatMoneyShort(r.benchMoney)}` : ` ${n(r.benchN)}`;
    return `${lead}${n(r.selN)} ${r.nNoun} · ${benchShort}${bm}`;
};

/**
 * "Sales performance" (compare mode, Phase 2): the selection's used sales
 * against the same benchmark as the panel above, from /api/branches/movements
 * and, for the money cards, the inventory units. Hero takeaway, grouped
 * measure cards (months of supply with its visible key) and the monthly
 * sell rate chart; a calm loading state and an error with Retry.
 */
export default function SalesPerformance({
    movements, loading = false, error = null, onRetry, allNames, selectedNames, selectionName, basis, benchName, benchShort,
    otherCount, invByBranch, inventoryReady = true, aging, chosenNames = [],
}) {
    const selected = useMemo(() => new Set(selectedNames), [selectedNames]);
    const oldest = aging.boundaries[2];
    const pools = useMemo(
        () => (movements ? perfPoolsByBranch(movements, allNames, inventoryReady ? invByBranch : null, { aging }) : null),
        [movements, allNames, invByBranch, inventoryReady, aging],
    );
    const single = selectedNames.length === 1;
    const view = useMemo(() => {
        if (!pools) return null;
        const branchPools = pools.names.map((nm) => pools.byName.get(nm));
        const selPool = addPerfPools(selectedNames.map((nm) => pools.byName.get(nm)).filter(Boolean));
        const bench = perfBenchmarkOf(basis, pools, selected, chosenNames);
        const dealer = addPerfPools(branchPools);
        const benchWho = benchmarkWho(basis, otherCount, chosenNames);
        const rows = buildPerfRows(selPool, bench, { aging }, branchPools, { benchShort, plural: !single, benchWho })
            // A money card without its field anywhere in the inventory is left out.
            .filter((r) => !(inventoryReady && r.id === 'agedValue' && dealer.priced === 0))
            .filter((r) => !(inventoryReady && r.id === 'carry' && dealer.carryN === 0))
            .map((r) => {
                if (r.id === 'agedValue') return { ...r, nText: moneyNLine(r, benchShort, 'aged') };
                if (r.id === 'carry') return { ...r, nText: moneyNLine(r, benchShort, 'accrued on current stock') };
                return r;
            });
        return {
            rows,
            series: sellRateSeries(selPool, bench),
        };
    }, [pools, selectedNames, selected, basis, chosenNames, otherCount, aging, benchShort, single, inventoryReady]);

    const isLoading = (r) => !inventoryReady && r.source === 'inventory';
    const takeaway = useMemo(() => {
        if (!view) return null;
        const { subject, plural } = selectionSubject(selectedNames);
        return compareTakeaway(view.rows.filter((r) => !isLoading(r)), {
            subject, plural, bench: benchmarkPhrase(basis, otherCount, chosenNames), openings: PERF_OPENINGS, oldest,
        });
    }, [view, selectedNames, basis, otherCount, chosenNames, inventoryReady, oldest]); // eslint-disable-line react-hooks/exhaustive-deps

    const asOf = movements?.as_of ? formatDay(Math.round(Date.parse(`${movements.as_of}T00:00:00Z`) / 86400000)) : null;
    const head = (
        <div className="br2-cmp-head">
            <h2 id="br2-perf-h">Sales performance</h2>
            <Tooltip label="About Sales performance" content={TITLE_TIP} />
            <span className="br2-sub" data-testid="perf-sub">Used units · last 12 months{asOf ? ` · as of ${asOf}` : ''}</span>
        </div>
    );

    if (!movements) {
        return (
            <section className="v2-card br2-cmp br2-perf" aria-labelledby="br2-perf-h" data-testid="perf-panel" aria-busy={loading && !error}>
                {head}
                {error ? (
                    <div className="br2-perf-state">
                        <ErrorState title="Sales history could not be loaded" message={error} onRetry={onRetry} />
                    </div>
                ) : (
                    <div className="br2-perf-state br2-perf-load" data-testid="perf-loading">
                        <p className="br2-sub" role="status">Loading sales history…</p>
                        <Skeleton lines={3} widths={['70%', '100%', '45%']} />
                    </div>
                )}
            </section>
        );
    }

    const byId = new Map(view.rows.map((r) => [r.id, r]));
    const statuses = MOS_STATUS;
    return (
        <section className="v2-card br2-cmp br2-perf" aria-labelledby="br2-perf-h" data-testid="perf-panel">
            {head}
            <CompareHero takeaway={takeaway} gapsTip={PERF_GAPS_TIP} testId="perf-takeaway">
                {!inventoryReady && <p className="br2-sub">The money figures follow once the inventory has loaded.</p>}
            </CompareHero>
            <CompareKey selectionName={selectionName} benchName={benchName} verdictTip={VERDICT_TIP} />
            <div className="br2-mgrid br2-mgrid--perf" aria-label="Sales measures">
                {PERF_GROUPS.map((g) => {
                    const groupRows = g.ids.map((id) => byId.get(id)).filter(Boolean);
                    if (!groupRows.length) return null;
                    return (
                        <section key={g.title} className={`br2-mgroup br2-mgroup--${groupRows.length}`} aria-label={g.title}>
                            <h3 className="v2-kpi-title" data-testid="perf-group">{g.title}</h3>
                            <ul className="br2-mcards">
                                {groupRows.map((r) => (
                                    <MeasureCard
                                        key={r.id}
                                        r={r}
                                        benchShort={benchShort}
                                        benchNames={basis === 'chosen' ? chosenNames : []}
                                        loading={isLoading(r)}
                                        loadingLabel={LOADING_INV}
                                        single={single}
                                        statuses={r.id === 'mos' ? statuses : null}
                                        smallTip={SMALL_TIP}
                                    />
                                ))}
                            </ul>
                        </section>
                    );
                })}
            </div>
            {/* Below the cards, not inside one, so every card of a row keeps one height. */}
            {byId.has('mos') && <MosLegend single={single} />}
            <div className="br2-cmp-catw">
                <SellRateChart
                    series={view.series}
                    win={pools.win}
                    selectionName={selectionName}
                    benchName={benchName}
                    median={basis === 'median'}
                />
            </div>
            <HowToRead basis={basis} oldest={oldest} chosenNames={chosenNames} />
        </section>
    );
}

SalesPerformance.propTypes = {
    movements: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.string,
    onRetry: PropTypes.func,
    allNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectionName: PropTypes.string.isRequired,
    basis: PropTypes.string.isRequired,
    benchName: PropTypes.string.isRequired,
    benchShort: PropTypes.string.isRequired,
    otherCount: PropTypes.number.isRequired,
    invByBranch: PropTypes.object,
    inventoryReady: PropTypes.bool,
    aging: PropTypes.shape({ boundaries: PropTypes.arrayOf(PropTypes.number) }).isRequired,
    chosenNames: PropTypes.arrayOf(PropTypes.string),
};
